import type { Status } from "~/types/Status";
import type { Equipment } from "~/types/Equipment";
import {
  EventStatus,
  type Event,
  type EventEquipmentOption,
  type EventSubtype,
  type EventType,
} from "~/types/Event";

export const EVENT_TYPE_INFRACTION = 1;
export const EVENT_TYPE_INCIDENT = 2;

export type EventSubtypeDictionary = Record<number, EventSubtype[]>;

export type GroupedEventSubtypesResponse = {
  infraction_types: EventSubtype[];
  incident_types: EventSubtype[];
  event_subtypes: EventSubtypeDictionary;
};

export type EventEquipmentSyncPayload = {
  equipment_id: number | null;
  equipment_not_repertoried: boolean;
};

type ApiClient = (
  url: string,
  options?: {
    method?: string;
    body?: unknown;
  },
) => Promise<unknown>;

type NormalizableEventType = Partial<EventType> & Record<string, unknown>;
type NormalizableEvent = Partial<Event> & Record<string, unknown>;
type NormalizableEquipment = Partial<Equipment> & Record<string, unknown>;
type EventStatusOption = Pick<Status, "id" | "label" | "text_color_class" | "bg_color_class" | "dark_text_color_class" | "dark_bg_color_class">;

const URGENT_STATUS_FALLBACK: EventStatusOption = {
  id: EventStatus.URGENT,
  label: "Urgent",
  text_color_class: "text-red-700",
  bg_color_class: "bg-red-100",
  dark_text_color_class: "text-red-300",
  dark_bg_color_class: "bg-red-900/30",
};

const parseSubtypeArray = (value: unknown): EventSubtype[] => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map(item => normalizeEventSubtype(item))
      .filter((item): item is EventSubtype => Boolean(item));
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      return parseSubtypeArray(parsed);
    }
    catch {
      return [];
    }
  }

  return [];
};

const parseBoolean = (value: unknown): boolean | null => {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1 ? true : value === 0 ? false : Boolean(value);
  }

  if (typeof value === "string") {
    switch (value.trim().toLowerCase()) {
      case "1":
      case "true":
      case "yes":
      case "oui":
        return true;
      case "0":
      case "false":
      case "no":
      case "non":
        return false;
      default:
        return Boolean(value);
    }
  }

  return Boolean(value);
};

export const normalizeEventSubtype = (
  subtype: Partial<EventSubtype> | null | undefined,
): EventSubtype | null => {
  if (!subtype || subtype.id === undefined || subtype.id === null) {
    return null;
  }

  return {
    id: Number(subtype.id),
    name: subtype.name ?? "",
    event_type_id: Number(subtype.event_type_id ?? 0),
    created_at: subtype.created_at,
    updated_at: subtype.updated_at,
  };
};

export const normalizeLinkedEquipment = (
  equipment: NormalizableEquipment | null | undefined,
): Partial<Equipment> | null => {
  if (!equipment || equipment.id === undefined || equipment.id === null) {
    return null;
  }

  return {
    ...equipment,
    id: Number(equipment.id),
    name: equipment.name ?? `Équipement #${equipment.id}`,
  };
};

export const normalizeEventEquipmentOption = (
  option: Partial<EventEquipmentOption> | null | undefined,
): EventEquipmentOption | null => {
  if (!option || option.id === undefined || option.id === null) {
    return null;
  }

  return {
    id: Number(option.id),
    name: option.name ?? `Équipement #${option.id}`,
    reference: option.reference ?? null,
    serial_number: option.serial_number ?? null,
    registration_number: option.registration_number ?? null,
    equipment_type: option.equipment_type ?? null,
    assigned_to: option.assigned_to ?? null,
    maintenance_site: option.maintenance_site ?? null,
    organilog_equipment_id: option.organilog_equipment_id ?? null,
  };
};

export const normalizeEventEquipmentOptions = (
  options: Array<Partial<EventEquipmentOption>> | null | undefined,
): EventEquipmentOption[] =>
  (options ?? [])
    .map(option => normalizeEventEquipmentOption(option))
    .filter((option): option is EventEquipmentOption => Boolean(option));

export const normalizeEventType = (
  eventType: NormalizableEventType,
): EventType => {
  const eventTypeId = Number(eventType.id);
  const eventSubtypes = parseSubtypeArray(
    eventType.event_subtypes ?? eventType["eventSubtypes"],
  );
  const merchantInfractionTypes = parseSubtypeArray(
    eventType.merchant_infraction_types
    ?? (eventTypeId === EVENT_TYPE_INFRACTION ? eventSubtypes : null),
  );
  const incidentTypes = parseSubtypeArray(
    eventType.incident_types
    ?? (eventTypeId === EVENT_TYPE_INCIDENT ? eventSubtypes : null),
  );

  return {
    id: eventTypeId,
    name: eventType.name ?? "",
    event_subtypes: eventSubtypes,
    merchant_infraction_types: merchantInfractionTypes,
    incident_types: incidentTypes,
  };
};

export const normalizeEventTypes = (
  eventTypes: NormalizableEventType[] | null | undefined,
): EventType[] => (eventTypes ?? []).map(normalizeEventType);

export const normalizeEvent = <T extends NormalizableEvent>(
  event: T,
): Event & T => {
  const rawStatus = Number(event.status ?? EventStatus.NEW);
  const explicitUrgent = parseBoolean(event.is_urgent);
  const normalizedWorkflowStatus = Number(
    event.workflow_status
    ?? event["workflowStatus"]
    ?? (rawStatus === EventStatus.URGENT ? EventStatus.NEW : rawStatus),
  );
  const normalizedIsUrgent = explicitUrgent
    ?? (event.workflow_status == null && rawStatus === EventStatus.URGENT);
  const normalizedStatus = (
    normalizedIsUrgent && normalizedWorkflowStatus !== EventStatus.CLOSED
      ? EventStatus.URGENT
      : normalizedWorkflowStatus
  );
  const normalizedSubtype = normalizeEventSubtype(
    event.event_subtype
    ?? event["eventSubtype"]
    ?? event.infraction_type
    ?? event.incident_type,
  );
  const normalizedSubtypeId = event.event_subtype_id
    ?? event.infraction_type_id
    ?? normalizedSubtype?.id
    ?? null;
  const normalizedPrimaryEquipment = normalizeLinkedEquipment(
    (event.equipment as NormalizableEquipment | null | undefined)
    ?? null,
  );
  const normalizedEquipments = Array.isArray(event.equipments)
    ? event.equipments
      .map(equipment => normalizeLinkedEquipment(equipment as NormalizableEquipment))
      .filter((equipment): equipment is Equipment => Boolean(equipment)) as Equipment[]
    : normalizedPrimaryEquipment
      ? [normalizedPrimaryEquipment as Equipment]
      : [];
  const primaryEquipment = normalizedPrimaryEquipment ?? normalizedEquipments[0] ?? null;

  return {
    ...event,
    status: normalizedStatus,
    workflow_status: normalizedWorkflowStatus,
    is_urgent: normalizedIsUrgent,
    event_subtype_id: normalizedSubtypeId,
    event_subtype: normalizedSubtype,
    infraction_type_id: event.infraction_type_id ?? normalizedSubtypeId,
    infraction_type: normalizeEventSubtype(event.infraction_type) ?? normalizedSubtype,
    incident_type: normalizeEventSubtype(event.incident_type)
      ?? (normalizedSubtype?.event_type_id === EVENT_TYPE_INCIDENT
        ? normalizedSubtype
        : null),
    equipment_id: event.equipment_id ?? primaryEquipment?.id ?? null,
    equipment: primaryEquipment,
    equipments: normalizedEquipments,
    equipment_not_repertoried: parseBoolean(event.equipment_not_repertoried) ?? false,
    organilog_intervention_id: event.organilog_intervention_id ?? null,
    organilog_intervention_url: event.organilog_intervention_url ?? null,
    ocv_event_url: event.ocv_event_url ?? null,
  } as Event & T;
};

export const normalizeEvents = <T extends NormalizableEvent>(
  events: T[] | null | undefined,
): Array<Event & T> => (events ?? []).map(event => normalizeEvent(event));

export const getNormalizedEventSubtype = (
  event: Partial<Event> | null | undefined,
): EventSubtype | null => {
  if (!event) {
    return null;
  }

  const normalizedEvent = normalizeEvent(event as NormalizableEvent);

  return normalizeEventSubtype(
    normalizedEvent.event_subtype
    ?? normalizedEvent.infraction_type
    ?? normalizedEvent.incident_type,
  );
};

export const getPrimaryEventEquipment = (
  event: Partial<Event> | null | undefined,
): Partial<Equipment> | null => {
  if (!event) {
    return null;
  }

  const normalizedEvent = normalizeEvent(event as NormalizableEvent);

  return normalizeLinkedEquipment(
    (normalizedEvent.equipment as NormalizableEquipment | null | undefined)
    ?? (normalizedEvent.equipments?.[0] as NormalizableEquipment | undefined)
    ?? null,
  );
};

export const isEventUrgent = (
  event: Partial<Event> | null | undefined,
): boolean => {
  if (!event) {
    return false;
  }

  return Boolean(normalizeEvent(event as NormalizableEvent).is_urgent);
};

export const getEventWorkflowStatus = (
  event: Partial<Event> | null | undefined,
): number => {
  if (!event) {
    return EventStatus.NEW;
  }

  return Number(normalizeEvent(event as NormalizableEvent).workflow_status ?? EventStatus.NEW);
};

export const getEventDisplayStatusId = (
  event: Partial<Event> | null | undefined,
): number => {
  if (!event) {
    return EventStatus.NEW;
  }

  return Number(normalizeEvent(event as NormalizableEvent).status ?? EventStatus.NEW);
};

export const getEventDisplayStatusOption = (
  event: Partial<Event> | null | undefined,
  statuses: EventStatusOption[] | null | undefined,
  fallbackStatus: EventStatusOption | null = null,
): EventStatusOption | null => {
  const displayStatusId = getEventDisplayStatusId(event);
  const matchedStatus = (statuses ?? []).find(status => Number(status.id) === displayStatusId);

  if (matchedStatus) {
    return matchedStatus;
  }

  if (displayStatusId === EventStatus.URGENT) {
    return URGENT_STATUS_FALLBACK;
  }

  return fallbackStatus;
};

export const getEventDisplayStatusLabel = (
  event: Partial<Event> | null | undefined,
  options: {
    statuses?: EventStatusOption[] | null;
    fallbackLabel?: string;
    urgentLabel?: string;
  } = {},
): string => {
  const displayStatusId = getEventDisplayStatusId(event);

  if (displayStatusId === EventStatus.URGENT) {
    return options.urgentLabel ?? "Urgent";
  }

  return getEventDisplayStatusOption(event, options.statuses)?.label
    ?? options.fallbackLabel
    ?? "—";
};

export const getEventDisplayStatusClasses = (
  event: Partial<Event> | null | undefined,
  options: {
    statuses?: EventStatusOption[] | null;
    fallbackClasses?: string;
  } = {},
): string => {
  const displayStatus = getEventDisplayStatusOption(event, options.statuses);

  if (!displayStatus) {
    return options.fallbackClasses ?? "";
  }

  return [displayStatus.bg_color_class, displayStatus.text_color_class]
    .filter(Boolean)
    .join(" ");
};

export const getEventSubtypeDisplayName = (
  event: Partial<Event> | null | undefined,
  options: {
    fallback?: string;
    fallbackToTitle?: boolean;
  } = {},
): string => {
  const subtype = getNormalizedEventSubtype(event);

  if (subtype?.name) {
    return subtype.name;
  }

  if ((event?.event_subtype_id ?? event?.infraction_type_id) && !subtype) {
    return "Sous-type inconnu";
  }

  if (options.fallbackToTitle && event?.title) {
    return event.title;
  }

  return options.fallback ?? "—";
};

export const getEventEquipmentDisplayName = (
  event: Partial<Event> | null | undefined,
  options: {
    fallback?: string;
    notRepertoriedLabel?: string;
  } = {},
): string => {
  if (!event) {
    return options.fallback ?? "—";
  }

  const equipment = getPrimaryEventEquipment(event);

  if (equipment?.name) {
    return equipment.name;
  }

  if (normalizeEvent(event as NormalizableEvent).equipment_not_repertoried) {
    return options.notRepertoriedLabel ?? "Équipement non répertorié";
  }

  return options.fallback ?? "—";
};

export const normalizeGroupedEventSubtypes = (
  payload: Record<string, unknown> | null | undefined,
): GroupedEventSubtypesResponse => {
  const rawGrouped = payload?.event_subtypes ?? {};
  const eventSubtypes: EventSubtypeDictionary = {};

  Object.entries(rawGrouped).forEach(([eventTypeId, items]) => {
    eventSubtypes[Number(eventTypeId)] = parseSubtypeArray(items);
  });

  const infractionTypes = parseSubtypeArray(payload?.infraction_types);
  const incidentTypes = parseSubtypeArray(payload?.incident_types);

  if (infractionTypes.length > 0) {
    eventSubtypes[EVENT_TYPE_INFRACTION] = infractionTypes;
  }

  if (incidentTypes.length > 0) {
    eventSubtypes[EVENT_TYPE_INCIDENT] = incidentTypes;
  }

  return {
    infraction_types: eventSubtypes[EVENT_TYPE_INFRACTION] ?? infractionTypes,
    incident_types: eventSubtypes[EVENT_TYPE_INCIDENT] ?? incidentTypes,
    event_subtypes: eventSubtypes,
  };
};

export const flattenEventSubtypes = (
  eventSubtypesByEventTypeId: EventSubtypeDictionary,
): EventSubtype[] =>
  Object.values(eventSubtypesByEventTypeId)
    .flat()
    .sort((left, right) => left.name.localeCompare(right.name, "fr"));

export const getEventSubtypesForEventTypes = (
  eventSubtypesByEventTypeId: EventSubtypeDictionary,
  eventTypeIds: number[] | null | undefined,
): EventSubtype[] => {
  if (!eventTypeIds || eventTypeIds.length === 0) {
    return flattenEventSubtypes(eventSubtypesByEventTypeId);
  }

  return eventTypeIds
    .flatMap(eventTypeId => eventSubtypesByEventTypeId[eventTypeId] ?? [])
    .sort((left, right) => left.name.localeCompare(right.name, "fr"));
};

export const fetchEventSubtypes = async (
  client: ApiClient,
  params: {
    event_type_id?: number | null;
    grouped?: boolean;
  } = {},
): Promise<EventSubtype[] | GroupedEventSubtypesResponse> => {
  const query = new URLSearchParams();

  if (params.event_type_id) {
    query.set("event_type_id", String(params.event_type_id));
  }

  if (params.grouped) {
    query.set("grouped", "true");
  }

  const suffix = query.toString() ? `?${query.toString()}` : "";
  const response = await client(`/api/mobile/event-subtypes${suffix}`, {
    method: "GET",
  });

  if (params.grouped) {
    return normalizeGroupedEventSubtypes(
      response as Record<string, unknown> | null | undefined,
    );
  }

  return parseSubtypeArray(response);
};

export const getInfractionTypes = async (
  client: ApiClient,
): Promise<EventSubtype[]> => {
  const response = await fetchEventSubtypes(client, {
    event_type_id: EVENT_TYPE_INFRACTION,
  });

  return Array.isArray(response) ? response : response.infraction_types;
};

export const fetchEventEquipmentOptions = async (
  client: ApiClient,
  params: {
    market_id: number | string;
    merchant_id?: number | string | null;
  },
): Promise<EventEquipmentOption[]> => {
  const query = new URLSearchParams({
    market_id: String(params.market_id),
  });

  if (params.merchant_id) {
    query.set("merchant_id", String(params.merchant_id));
  }

  const response = await client(`/api/events/equipment-options?${query.toString()}`, {
    method: "GET",
  }) as { data?: Array<Partial<EventEquipmentOption>> };

  return normalizeEventEquipmentOptions(response?.data);
};

export const syncEventEquipment = async (
  client: ApiClient,
  eventId: number | string,
  payload: EventEquipmentSyncPayload,
): Promise<Event> => {
  const response = await client(`/api/events/${eventId}/equipment-sync`, {
    method: "POST",
    body: payload,
  }) as { data?: NormalizableEvent };

  return normalizeEvent((response?.data ?? {}) as NormalizableEvent);
};
